import type { BookingResponse } from '@/shared/api';
import { formatBookingPrice, isPriceUnderReview } from '@/shared/lib/bookingPrice';
import { classifyChannel, type ChannelMeta } from '../../calendar/calendarHelpers';
import { bookingStatusLabel } from '../components/bookingsFormat';
import { formatOccupancy } from '@/shared/lib/occupancy';

export interface AgendaPill {
  label: string;
  tone: 'amber' | 'red';
}

export interface AgendaCardModel {
  /** Gastname; wer keine Gastdaten sieht, liest „Buchung {Referenz}“. */
  title: string;
  /** Buchungsnummer; ohne Gastdaten steht sie im Titel, hier dann der Kanal. */
  reference: string;
  people: string;
  pills: AgendaPill[];
  from: string;
  to: string;
  nights: number;
  /** Bereits verbrachte Nächte, nur im Haus. */
  doneNights: number | null;
  channel: ChannelMeta;
  amount: string;
  priceUnderReview: boolean;
  cancelled: boolean;
}

const MANUAL = classifyChannel('Manuell');
const DAY = 86_400_000;

const dayOf = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00Z`);
const daysBetween = (from: string, to: string) =>
  Math.round((dayOf(to).getTime() - dayOf(from).getTime()) / DAY);

const weekday = new Intl.DateTimeFormat('de-DE', { weekday: 'short', timeZone: 'UTC' });

/** „Mo 19.10.“ – kurz genug für eine Zeile mit Pfeil und Nächten. */
export function agendaDate(iso: string): string {
  const date = dayOf(iso);
  return `${weekday.format(date).replace('.', '')} ${date.getUTCDate()}.${date.getUTCMonth() + 1}.`;
}

/** Nur Abweichungen: ob jemand kommt, da ist oder geht, sagt schon der Abschnitt. */
function pills(row: BookingResponse): AgendaPill[] {
  const result: AgendaPill[] = [];
  if (row.status === 'Pending')
    result.push({ label: bookingStatusLabel(row.status, row.source), tone: 'amber' });
  if (row.status === 'Cancelled' || row.status === 'NoShow') {
    result.push({ label: bookingStatusLabel(row.status, row.source), tone: 'red' });
  }
  if (row.stayPhase === 'DataQuality') result.push({ label: 'Daten prüfen', tone: 'amber' });
  return result;
}

export function agendaCard(
  row: BookingResponse,
  canSeeGuests: boolean,
  today: string,
): AgendaCardModel {
  const channel = row.source === 'Manuell' ? MANUAL : classifyChannel(row.channel);
  const nights = Math.max(0, daysBetween(row.checkInDate, row.checkOutDate));
  // Im Aufenthalt nach Datum (Heute des Servers, sonst des Objekts): auch ausstehende Gäste im Haus.
  const day = row.objectToday ?? today;
  const staying =
    row.status !== 'Cancelled' &&
    row.checkInDate < day &&
    day < (row.actualDepartureDate ?? row.checkOutDate);
  const done = staying ? Math.min(nights - 1, daysBetween(row.checkInDate, day)) : null;
  return {
    title: canSeeGuests ? (row.guestName ?? 'Ohne Gastnamen') : `Buchung ${row.bookingReference}`,
    reference: canSeeGuests ? row.bookingReference : channel.label,
    people: formatOccupancy(row),
    pills: pills(row),
    from: agendaDate(row.checkInDate),
    to: agendaDate(row.checkOutDate),
    nights,
    doneNights: done,
    channel,
    amount: formatBookingPrice(row),
    priceUnderReview: isPriceUnderReview(row),
    cancelled: row.status === 'Cancelled',
  };
}
